import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@fieldcheck/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copia al portapapeles un informe de diagnóstico pegable.
 *
 * El componente solo RECOGE datos y delega el formato en
 * `buildDiagnosticsReport` (shared, función pura y testeada). Si el informe se
 * armara aquí, cada app tendría su versión y divergirían en cuanto alguien
 * añadiera un campo.
 *
 * Etiquetas: se usan `Copy` y `Copied!`, que ya están traducidas en los once
 * idiomas. Una cadena nueva como "Copy diagnostics" saldría en INGLÉS en todos
 * ellos sin que el gate de i18n se queje, porque solo valida lo que ya está en
 * el catálogo.
 */
/** Cuánto se mantiene el "¡Copiado!" antes de volver a la etiqueta normal. */
const MS_CONFIRMACION = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** El MISMO modelo que la pantalla renderiza. */
  secciones: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const [copiado, setCopiado] = createSignal(false);
  const [error, setError] = createSignal(false);

  const copiar = async () => {
    const texto = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: props.secciones(),
    });
    try {
      await navigator.clipboard.writeText(texto);
      setError(false);
      setCopiado(true);
      setTimeout(() => setCopiado(false), MS_CONFIRMACION);
    } catch {
      // El portapapeles falla sin permiso o fuera de contexto seguro. Callarlo
      // dejaría al usuario creyendo que copió: se marca el fallo y se deja el
      // informe seleccionable como plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void copiar()}
      >
        <Show when={copiado()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {copiado() ? t(msg`Copied!`) : t(msg`Copy`)}
      </Button>

      {/* Plan B cuando el portapapeles no está disponible: el informe a la vista
          para seleccionarlo a mano. Sin esto, el botón sería un callejón sin
          salida justo en los entornos donde más falta hace depurar. */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: props.secciones(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
